import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../App';
import { mockExpenses, EXPENSE_TYPES } from '../utils/mockData';

function formatMoney(n) { return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function BarRow({ label, value, max, color, sub }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 13, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, flexShrink: 0 }}>{formatMoney(value)}</span>
      </div>
      <div className="progress-bar"><div className="progress-fill" style={{ width: `${pct}%`, background: color }} /></div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export default function Reports() {
  const { demoMode } = useContext(AppContext);
  const [expenses, setExpenses] = useState([]);
  const [tab, setTab] = useState('jobs');

  useEffect(() => { setExpenses(demoMode ? mockExpenses : []); }, [demoMode]);

  const total = expenses.reduce((s, e) => s + Number(e.amount), 0);
  const byJob = [...new Set(expenses.map(e => e.jobName))].map(job => {
    const items = expenses.filter(e => e.jobName === job);
    return { name: job, total: items.reduce((s, e) => s + Number(e.amount), 0), count: items.length,
      breakdown: EXPENSE_TYPES.map(t => ({ ...t, value: items.filter(e => e.type === t.id).reduce((s, e) => s + Number(e.amount), 0) })).filter(t => t.value > 0) };
  }).sort((a, b) => b.total - a.total);

  const byCategory = EXPENSE_TYPES.map(t => ({ ...t, total: expenses.filter(e => e.type === t.id).reduce((s, e) => s + Number(e.amount), 0), count: expenses.filter(e => e.type === t.id).length })).filter(t => t.total > 0).sort((a, b) => b.total - a.total);

  const byMonth = expenses.reduce((acc, e) => { const key = e.date.slice(0, 7); if (!acc[key]) acc[key] = { month: key, total: 0, count: 0 }; acc[key].total += Number(e.amount); acc[key].count++; return acc; }, {});
  const monthList = Object.values(byMonth).sort((a, b) => b.month.localeCompare(a.month));
  const maxMonth = Math.max(...monthList.map(m => m.total), 1);

  const tabStyle = (t) => ({ flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: 0.5, fontFamily: 'var(--font-body)', textTransform: 'uppercase', borderBottom: tab === t ? '2px solid var(--accent-orange)' : '2px solid transparent', background: 'none', color: tab === t ? 'var(--accent-orange)' : 'var(--text-muted)', transition: 'color 0.15s' });

  return (
    <div className="fade-in">
      <div className="screen-header" style={{ paddingBottom: 12 }}>
        <div><div className="screen-title">REPORTS</div><div className="screen-subtitle">Total: {formatMoney(total)}</div></div>
      </div>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', margin: '0 16px 4px', gap: 0 }}>
        <button style={tabStyle('jobs')} onClick={() => setTab('jobs')}>By Job</button>
        <button style={tabStyle('category')} onClick={() => setTab('category')}>By Type</button>
        <button style={tabStyle('time')} onClick={() => setTab('time')}>By Month</button>
      </div>

      {tab === 'jobs' && (
        <div>
          {byJob.length === 0 ? <div className="empty-state"><div className="empty-state__icon">🏗️</div><div className="empty-state__title">No job data yet</div></div> :
            byJob.map((job, i) => (
              <div key={i} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{job.count} expense{job.count !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 500, color: 'var(--accent-orange)', flexShrink: 0 }}>{formatMoney(job.total)}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {job.breakdown.map(t => (<div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: `${t.color}15`, borderRadius: 6, padding: '4px 8px' }}><span style={{ fontSize: 12 }}>{t.icon}</span><span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: t.color, fontWeight: 600 }}>{formatMoney(t.value)}</span></div>))}
                </div>
                <div className="progress-bar" style={{ marginTop: 10 }}><div className="progress-fill" style={{ width: `${total > 0 ? (job.total / total) * 100 : 0}%`, background: 'var(--accent-orange)' }} /></div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{total > 0 ? ((job.total / total) * 100).toFixed(1) : 0}% of all spending</div>
              </div>
            ))}
        </div>
      )}

      {tab === 'category' && (
        <div className="card">
          {byCategory.length === 0 ? <div className="empty-state" style={{ padding: '40px 0' }}><div className="empty-state__icon">📊</div><div className="empty-state__title">No data yet</div></div> : (
            <>
              {byCategory.map(t => (<BarRow key={t.id} label={`${t.icon} ${t.label}`} value={t.total} max={byCategory[0]?.total || 1} color={t.color} sub={`${t.count} expense${t.count !== 1 ? 's' : ''} · ${total > 0 ? ((t.total / total) * 100).toFixed(1) : 0}% of total`} />))}
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Total</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 600 }}>{formatMoney(total)}</span>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'time' && (
        <div className="card">
          {monthList.length === 0 ? <div className="empty-state" style={{ padding: '40px 0' }}><div className="empty-state__icon">📅</div><div className="empty-state__title">No data yet</div></div> :
            monthList.map((m, i) => { const [year, mon] = m.month.split('-'); const monthName = new Date(year, parseInt(mon) - 1).toLocaleString('default', { month: 'long' }); return (<BarRow key={i} label={`${monthName} ${year}`} value={m.total} max={maxMonth} color="var(--accent-blue)" sub={`${m.count} expense${m.count !== 1 ? 's' : ''}`} />); })}
        </div>
      )}

      <div style={{ margin: '8px 16px', padding: '14px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Tax & Export</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>Gas mileage at $0.67/mi and material/labor costs are fully deductible business expenses. Export your DynamoDB data as CSV from the AWS Console for your accountant.</div>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
